import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Phone, Mail, Star, CheckCircle, Briefcase } from 'lucide-react';
import { db } from '../../firebase/config';
import { doc, updateDoc, getDoc } from 'firebase/firestore';

export default function InterestedContractorsList({ job, onClose, onAssign }) {
    const [contractors, setContractors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [assigning, setAssigning] = useState(null);

    useEffect(() => {
        loadInterestedContractors();
    }, [job]);

    const loadInterestedContractors = async () => {
        if (!job.interestedContractors || job.interestedContractors.length === 0) {
            setContractors([]);
            setLoading(false);
            return;
        }

        try {
            // Fetch full contractor details for each interested contractor
            const contractorPromises = job.interestedContractors.map(async (interest) => {
                const contractorRef = doc(db, 'contractors', interest.contractorId);
                const contractorSnap = await getDoc(contractorRef);
                
                if (contractorSnap.exists()) {
                    return {
                        ...contractorSnap.data(),
                        uid: interest.contractorId,
                        expressedAt: interest.expressedAt
                    };
                }
                return null;
            });

            const contractorData = await Promise.all(contractorPromises);
            const validContractors = contractorData.filter(c => c !== null);
            
            // Sort by expression time (most recent first)
            validContractors.sort((a, b) => 
                new Date(b.expressedAt) - new Date(a.expressedAt)
            );

            setContractors(validContractors);
        } catch (error) {
            console.error('Error loading interested contractors:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAssignContractor = async (contractor) => {
        setAssigning(contractor.uid);

        try {
            // Update job with assigned contractor
            await updateDoc(doc(db, 'workOrders', job.id), {
                contractorId: contractor.uid,
                contractorName: contractor.name,
                contractorEmail: contractor.email,
                assignedAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                // Remove from contractor pool once assigned
                visibleToContractors: false
            });

            if (onAssign) {
                onAssign(contractor);
            }

            onClose();
        } catch (error) {
            console.error('Error assigning contractor:', error);
            alert('Failed to assign contractor. Please try again.');
        } finally {
            setAssigning(null);
        }
    };

    const getPhaseInfo = (phase) => {
        const phases = {
            'shadow': { name: 'Shadow', color: 'bg-gray-100 text-gray-800' },
            'trainee': { name: 'Trainee', color: 'bg-blue-100 text-blue-800' },
            'junior': { name: 'Junior Tech', color: 'bg-purple-100 text-purple-800' },
            'standard': { name: 'Standard Tech', color: 'bg-green-100 text-green-800' },
            'experienced': { name: 'Experienced Tech', color: 'bg-yellow-100 text-yellow-800' }
        };
        return phases[phase] || phases['trainee'];
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">
                                Interested Contractors
                            </h2>
                            <p className="text-sm text-gray-600 mt-1">
                                {job.serviceType} - {job.location}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 overflow-y-auto max-h-[calc(80vh-88px)]">
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : contractors.length === 0 ? (
                            <div className="text-center py-12">
                                <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    No Interested Contractors Yet
                                </h3>
                                <p className="text-gray-600">
                                    Contractors haven't expressed interest in this job yet.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {contractors.map((contractor) => {
                                    const phaseInfo = getPhaseInfo(contractor.phase);
                                    const isAssigning = assigning === contractor.uid;

                                    return (
                                        <motion.div
                                            key={contractor.uid}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    {/* Contractor Info */}
                                                    <div className="flex items-center space-x-3 mb-3">
                                                        <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                                                            <User className="w-6 h-6 text-purple-600" />
                                                        </div>
                                                        <div>
                                                            <h3 className="font-semibold text-gray-900">
                                                                {contractor.name}
                                                            </h3>
                                                            <div className="flex items-center space-x-2 mt-1">
                                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${phaseInfo.color}`}>
                                                                    {phaseInfo.name}
                                                                </span>
                                                                <span className="text-sm text-gray-600">
                                                                    ${contractor.hourlyRate}/hr
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Contact Info */}
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                                                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                                                            <Mail className="w-4 h-4" />
                                                            <span>{contractor.email}</span>
                                                        </div>
                                                        {contractor.phone && (
                                                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                                                                <Phone className="w-4 h-4" />
                                                                <span>{contractor.phone}</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Stats */}
                                                    {contractor.metrics && (
                                                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                                                            <div className="flex items-center space-x-1">
                                                                <Briefcase className="w-4 h-4" />
                                                                <span>{contractor.metrics.totalJobs || 0} jobs</span>
                                                            </div>
                                                            {contractor.metrics.completionRate && (
                                                                <div className="flex items-center space-x-1">
                                                                    <CheckCircle className="w-4 h-4" />
                                                                    <span>{contractor.metrics.completionRate}% completion</span>
                                                                </div>
                                                            )}
                                                            {contractor.metrics.averageRating && (
                                                                <div className="flex items-center space-x-1">
                                                                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                                                    <span>{contractor.metrics.averageRating.toFixed(1)}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Expression Time */}
                                                    <p className="text-xs text-gray-500 mt-2">
                                                        Expressed interest {new Date(contractor.expressedAt).toLocaleString()}
                                                    </p>
                                                </div>

                                                {/* Assign Button */}
                                                <button
                                                    onClick={() => handleAssignContractor(contractor)}
                                                    disabled={isAssigning}
                                                    className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                                >
                                                    {isAssigning ? 'Assigning...' : 'Assign Job'}
                                                </button>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
